import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { MeContext } from '../../../../context/MeContext';
import type { MeContextState } from '../../../../store/types/account';
import { useGetPassQuery } from '../../../../store/redux/api/passApi';
import { useGetPassTemplateQuery } from '../../../../store/redux/api/templateApi';
import { useAppSelector } from '../../../../store/redux/hooks';
import { selectPass } from '../../../../store/redux/slices/pass.slice';
import { buildTemplateMeta } from '../utils/buildTemplateMeta';
import { hasNfcEnabled } from '../utils/passHelpers';
import type { PassBarcode } from '../../../../store/types/pass';

export const usePassBuilderData = (isCreate?: boolean) => {
  const { uuid } = useParams<{ uuid: string }>();
  const passState = useAppSelector(selectPass);

  const { data: passData } = useGetPassQuery(
    { uuid: uuid ?? '' },
    { skip: !uuid, refetchOnMountOrArgChange: true }
  );
  const pass = passData?.data?.data;
  const passTemplate = passData?.meta?.template;
  const templateId = passTemplate?.uuid || passState.templateUuid || '';

  const { data: templateData } = useGetPassTemplateQuery(
    { id: templateId, includeDynamicForm: true },
    { skip: !templateId, refetchOnMountOrArgChange: true }
  );

  const template =
    passTemplate ||
    (isCreate && templateId && templateData?.data?.id === templateId
      ? buildTemplateMeta(
          templateData.data as {
            id: string;
            attributes: Record<string, unknown>;
          }
        )
      : undefined);

  const dynamicForm = templateData?.included?.find(
    (inc) => inc.type === 'dynamicFormTemplate'
  );

  const meCtx = useContext(MeContext) as {
    state: MeContextState & {
      entities?: { uuid: string; id: string | number; nfcEnabled?: boolean }[];
    };
  };
  const { filterEntity, localisationEnabled } = meCtx.state as MeContextState & {
    filterEntity?: { uuid: string; id: string | number; nfcEnabled?: boolean };
    localisationEnabled?: boolean;
  };
  const templateEntity = template?.entity_id
    ? meCtx.state.entities?.find(
        (e) => String(e.id) === String(template.entity_id)
      )
    : undefined;
  const resolvedEntity =
    templateEntity ||
    filterEntity ||
    (meCtx.state.entities?.length === 1 ? meCtx.state.entities[0] : undefined);

  const templateAttrs = templateData?.data?.attributes as
    | {
        stampCard?: { stampCount?: number };
        loyaltyBalance?: { enabled?: boolean; numberType?: string };
        barcode?: { enabled?: boolean; type?: string; source?: string; value?: string; displayText?: boolean };
        nfc?: { enabled?: boolean; source?: string; customValue?: string };
        googleNfcEnabled?: { enabled?: boolean };
      }
    | undefined;

  const templateFields = (templateData?.data?.attributes as Record<string, unknown>)?.fields as
    | Record<string, Record<string, { localisation?: Record<string, unknown> } | null>>
    | undefined;

  const passContent = pass?.attributes?.passContent;
  const nfcRaw = pass?.attributes?.nfc;
  const headerOneId = template?.header?.header_one?.id;
  const currentStamps = (() => {
    if (!headerOneId || !passContent) return 0;
    const raw = (passContent as Record<string, unknown>)[headerOneId];
    const value = raw && typeof raw === 'object' && 'value' in raw
      ? (raw as { value: unknown }).value
      : raw;
    return parseInt(String(value ?? '0'), 10) || 0;
  })();

  return {
    uuid,
    pass,
    passState,
    template,
    templateData,
    templateAttrs,
    templateFields,
    dynamicForm,
    passContent,
    nfcRaw,
    resolvedEntity,
    localisationEnabled: !!localisationEnabled,
    distributionUrl: pass?.attributes?.downloadUrl,
    groupTags:
      isCreate || passState.isInitialised
        ? passState.groupTags
        : (pass?.attributes?.groupTags ?? []),
    isStampCard: template?.template_type === 'stampCard',
    totalStamps: templateAttrs?.stampCard?.stampCount ?? 0,
    currentStamps,
    templateEntityUuid: resolvedEntity?.uuid,
    nfcEntityEnabled: resolvedEntity?.nfcEnabled ?? false,
    hasNfcEnabled: hasNfcEnabled(nfcRaw),
    templateType: template?.template_type,
  };
};
